"use client";
import React, { useState, useEffect } from 'react';
import { BusinessProfile, IdealClient, ValueProp, CompanySize, GeoScope, BudgetRange } from '../../../types/biz';
import { loadBusiness, saveBusiness, loadIdealClients, saveIdealClient, loadValueProps, saveValueProp, updateProfileBizSnapshot } from '../../../lib/biz.store';
import { CompanyIcon, GoalIcon } from '../../icons';
import { Star } from 'lucide-react';

interface BusinessStepProps {
  uid: string;
  onNext: () => void;
  onBack: () => void;
}

const COMPANY_SIZES: { id: CompanySize; label: string }[] = [
  { id: 'solo', label: 'Solo ondernemer' },
  { id: 'smb', label: 'Klein bedrijf (2-50)' },
  { id: 'scaleup', label: 'Scale-up (50-500)' },
  { id: 'enterprise', label: 'Enterprise (500+)' }
];

const GEO_SCOPES: { id: GeoScope; label: string }[] = [
  { id: 'NL', label: 'Nederland' },
  { id: 'EU', label: 'Europa' },
  { id: 'Global', label: 'Wereldwijd' }
];

const BUDGET_RANGES: { id: BudgetRange; label: string }[] = [
  { id: 'low', label: '€1K - €10K' },
  { id: 'mid', label: '€10K - €50K' },
  { id: 'high', label: '€50K+' }
];

const INDUSTRY_SUGGESTIONS = [
  "SaaS", "E-commerce", "Consulting", "Marketing", "Finance", "Healthcare", 
  "Education", "Real Estate", "Manufacturing", "Retail", "Technology", "Media"
];

const REGION_SUGGESTIONS = [
  "Amsterdam", "Rotterdam", "Den Haag", "Utrecht", "Eindhoven", "Groningen",
  "Tilburg", "Almere", "Breda", "Nijmegen", "Enschede", "Apeldoorn"
];

export default function BusinessStep({ uid, onNext, onBack }: BusinessStepProps) {
  const [activeTab, setActiveTab] = useState<'business' | 'idealClient' | 'valueProp'>('business');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Business state
  const [business, setBusiness] = useState<Partial<BusinessProfile>>({
    companyName: '',
    kvkNumber: '',
    website: '',
    companySize: 'solo',
    industry: [],
    regions: [],
    mode: 'online',
    languages: [],
    description: '',
    socials: {}
  });
  
  // Ideal Client state
  const [idealClient, setIdealClient] = useState<Partial<IdealClient>>({
    title: '',
    sectors: [],
    companySizeRange: 'smb',
    roles: [],
    geo: 'NL',
    budgetRange: 'mid',
    pains: [],
    goals: [],
    mustHaves: [],
    niceToHaves: [],
    antiPersona: [],
    avgDealValue: undefined
  });
  
  // Value Proposition state
  const [valueProp, setValueProp] = useState<Partial<ValueProp>>({
    promise: '',
    differentiators: [],
    proof: [],
    elevator: '',
    offers: [],
    caseLinks: []
  });
  
  // Form inputs
  const [newIndustry, setNewIndustry] = useState('');
  const [newRegion, setNewRegion] = useState('');
  const [newLanguage, setNewLanguage] = useState('');
  const [newSector, setNewSector] = useState(''); // ✅ FIX: Sector input state
  const [newPain, setNewPain] = useState('');
  const [newGoal, setNewGoal] = useState('');
  const [newDifferentiator, setNewDifferentiator] = useState('');
  const [newProof, setNewProof] = useState('');

  // Load existing data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [businessData, idealClients, valueProps] = await Promise.all([
          loadBusiness(uid),
          loadIdealClients(uid),
          loadValueProps(uid)
        ]);
        
        if (businessData) {
          setBusiness(businessData);
        }
        
        if (idealClients.length > 0) {
          setIdealClient(idealClients[0]);
        }
        
        if (valueProps.length > 0) {
          setValueProp(valueProps[0]);
        }
      } catch (error) {
        console.error('Error loading business data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [uid]);

  const handleSaveBusiness = async () => {
    if (!business.companyName?.trim()) {
      alert('Bedrijfsnaam is verplicht');
      return;
    }
    
    // Validate required fields
    if (!business.regions || business.regions.length === 0) {
      alert('Selecteer minimaal één regio');
      return;
    }
    
    setSaving(true);
    try {
      await saveBusiness(uid, business);
      alert('✅ Business profiel opgeslagen!');
    } catch (error: any) {
      console.error('Error saving business:', error);
      const errorMessage = error?.message || error?.code || 'Onbekende fout';
      console.error('Full error details:', {
        code: error?.code,
        message: error?.message,
        stack: error?.stack,
        business: business
      });
      alert(`❌ Er ging iets mis bij het opslaan: ${errorMessage}\n\nControleer de browser console voor meer details.`);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveIdealClient = async () => {
    if (!idealClient.title?.trim()) {
      alert('Titel is verplicht');
      return;
    }
    
    if (idealClient.sectors?.length === 0) {
      alert('Selecteer minimaal één sector');
      return;
    }
    
    setSaving(true);
    try {
      await saveIdealClient(uid, idealClient.id || null, idealClient);
      alert('✅ Ideal Client opgeslagen!');
    } catch (error) {
      console.error('Error saving ideal client:', error);
      alert('❌ Er ging iets mis bij het opslaan');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveValueProp = async () => {
    if (!valueProp.promise?.trim()) {
      alert('Value proposition is verplicht');
      return;
    }
    
    if (!valueProp.elevator?.trim()) {
      alert('Elevator pitch is verplicht');
      return;
    }
    
    setSaving(true);
    try {
      await saveValueProp(uid, valueProp.id || null, valueProp);
      alert('✅ Value Proposition opgeslagen!');
    } catch (error) {
      console.error('Error saving value proposition:', error);
      alert('❌ Er ging iets mis bij het opslaan');
    } finally {
      setSaving(false);
    }
  };

  // Auto-save when data changes
  React.useEffect(() => {
    if (uid && !loading) {
      // Auto-save business data when it changes
      if (business.companyName?.trim()) {
        saveBusiness(uid, business).catch(error => {
          console.error('Error auto-saving business:', error);
        });
      }
      
      // Auto-save ideal client when it changes
      if (idealClient.title?.trim() && idealClient.sectors && idealClient.sectors.length > 0) {
        saveIdealClient(uid, idealClient.id || null, idealClient).catch(error => {
          console.error('Error auto-saving ideal client:', error);
        });
      }
      
      // Auto-save value proposition when it changes
      if (valueProp.promise?.trim() && valueProp.elevator?.trim()) {
        saveValueProp(uid, valueProp.id || null, valueProp).catch(error => {
          console.error('Error auto-saving value proposition:', error);
        });
      }
    }
  }, [business, idealClient, valueProp, uid, loading]);

  const addItem = (field: string, value: string, setter: any) => {
    if (value.trim() && !setter[field]?.includes(value.trim())) {
      setter((prev: any) => ({
        ...prev,
        [field]: [...(prev[field] || []), value.trim()]
      }));
    }
  };

  const removeItem = (field: string, index: number, setter: any) => {
    setter((prev: any) => ({
      ...prev,
      [field]: prev[field]?.filter((_: any, i: number) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2 flex items-center justify-center gap-2"><CompanyIcon size={28} weight="regular" className="text-gray-700" /> Business & Ideal Client</h2>
        <p className="text-gray-600">Vertel ons over je bedrijf en je ideale klant</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('business')}
          className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'business'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <span className="inline-flex items-center gap-1"><CompanyIcon size={16} weight="regular" /> Bedrijf</span>
        </button>
        <button
          onClick={() => setActiveTab('idealClient')}
          className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'idealClient'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <span className="inline-flex items-center gap-1"><GoalIcon size={16} weight="regular" /> Ideal Client</span>
        </button>
        <button
          onClick={() => setActiveTab('valueProp')}
          className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'valueProp'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <span className="inline-flex items-center gap-1"><Star size={16} /> Value Proposition</span>
        </button>
      </div>

      {/* Business Tab */}
      {activeTab === 'business' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2"><CompanyIcon size={22} weight="regular" className="text-gray-700" /> Bedrijfsprofiel</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bedrijfsnaam *
                </label>
                <input
                  type="text"
                  value={business.companyName || ''}
                  onChange={(e) => setBusiness(prev => ({ ...prev, companyName: e.target.value }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Jouw Bedrijf BV"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  KVK Nummer
                </label>
                <input
                  type="text"
                  value={business.kvkNumber || ''}
                  onChange={(e) => setBusiness(prev => ({ ...prev, kvkNumber: e.target.value }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="12345678"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Website
                </label>
                <input
                  type="url"
                  value={business.website || ''}
                  onChange={(e) => setBusiness(prev => ({ ...prev, website: e.target.value }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://jouwbedrijf.nl"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bedrijfsgrootte
                </label>
                <select
                  value={business.companySize || 'solo'}
                  onChange={(e) => setBusiness(prev => ({ ...prev, companySize: e.target.value as CompanySize }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {COMPANY_SIZES.map(size => (
                    <option key={size.id} value={size.id}>{size.label}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Industry & Regions */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Industrieën
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newIndustry}
                    onChange={(e) => setNewIndustry(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        addItem('industry', newIndustry, setBusiness);
                        setNewIndustry('');
                      }
                    }}
                    className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Voeg industrie toe..."
                  />
                  <button
                    onClick={() => {
                      addItem('industry', newIndustry, setBusiness);
                      setNewIndustry('');
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    +
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {business.industry?.map((item, index) => (
                    <span key={index} className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                      {item}
                      <button
                        onClick={() => removeItem('industry', index, setBusiness)}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Regio's *
                </label>
                {/* ✅ FIX: Dropdown selectie in plaats van text input */}
                <select
                  value={newRegion}
                  onChange={(e) => {
                    const selectedRegion = e.target.value;
                    if (selectedRegion && !business.regions?.includes(selectedRegion)) {
                      addItem('regions', selectedRegion, setBusiness);
                      setNewRegion('');
                    }
                  }}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Selecteer regio...</option>
                  <optgroup label="Nederlandse Provincies">
                    {['Noord-Holland', 'Zuid-Holland', 'Utrecht', 'Noord-Brabant', 'Gelderland', 'Overijssel', 'Limburg', 'Friesland', 'Groningen', 'Drenthe', 'Flevoland', 'Zeeland'].map((province) => (
                      <option key={province} value={province}>{province}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Algemene Regio's">
                    {['Nederland - Noord', 'Nederland - Randstad', 'Nederland - Oost', 'Nederland - Zuid'].map((region) => (
                      <option key={region} value={region}>{region}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Internationaal">
                    {['België', 'Duitsland', 'Frankrijk', 'Verenigd Koninkrijk', 'Verenigde Staten', 'Internationaal'].map((region) => (
                      <option key={region} value={region}>{region}</option>
                    ))}
                  </optgroup>
                </select>
                <p className="text-xs text-gray-500 mt-1">Regio’s waar jij klanten/oppdrchten zoekt (mag afwijken van je eigen vestigingslocatie).</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {business.regions?.map((item, index) => (
                    <span key={index} className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                      {item}
                      <button
                        onClick={() => removeItem('regions', index, setBusiness)}
                        className="ml-2 text-green-600 hover:text-green-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Beschrijving
            </label>
            <textarea
              value={business.description || ''}
              onChange={(e) => setBusiness(prev => ({ ...prev, description: e.target.value }))}
              rows={4}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Vertel kort over je bedrijf..."
            />
          </div>
          
          <div className="mt-6 flex justify-end">
            <button
              onClick={handleSaveBusiness}
              disabled={saving}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Opslaan...' : 'Opslaan'}
            </button>
          </div>
        </div>
      )}

      {/* Ideal Client Tab */}
      {activeTab === 'idealClient' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2"><GoalIcon size={22} weight="regular" className="text-gray-700" /> Ideal Client</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Titel *
              </label>
              <input
                type="text"
                value={idealClient.title || ''}
                onChange={(e) => setIdealClient(prev => ({ ...prev, title: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Marketing managers in SaaS"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Geografische scope
              </label>
              <select
                value={idealClient.geo || 'NL'}
                onChange={(e) => setIdealClient(prev => ({ ...prev, geo: e.target.value as GeoScope }))}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {GEO_SCOPES.map(geo => (
                  <option key={geo.id} value={geo.id}>{geo.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sectoren *
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newSector || ''}
                onChange={(e) => setNewSector(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    addItem('sectors', newSector, setIdealClient);
                    setNewSector('');
                  }
                }}
                className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Voeg sector toe (bijv. SaaS, E-commerce)..."
              />
              <button
                onClick={() => {
                  if (newSector?.trim()) {
                    addItem('sectors', newSector, setIdealClient);
                    setNewSector('');
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                +
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mb-2">
              {idealClient.sectors?.map((item, index) => (
                <span key={index} className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                  {item}
                  <button
                    onClick={() => removeItem('sectors', index, setIdealClient)}
                    className="ml-2 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            {(!idealClient.sectors || idealClient.sectors.length === 0) && (
              <p className="text-xs text-red-500 mt-1">⚠️ Selecteer minimaal één sector</p>
            )}
          </div>
          
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Pijnpunten
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newPain}
                onChange={(e) => setNewPain(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    addItem('pains', newPain, setIdealClient);
                    setNewPain('');
                  }
                }}
                className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Voeg pijnpunt toe..."
              />
              <button
                onClick={() => {
                  addItem('pains', newPain, setIdealClient);
                  setNewPain('');
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                +
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {idealClient.pains?.map((item, index) => (
                <span key={index} className="inline-flex items-center px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm">
                  {item}
                  <button
                    onClick={() => removeItem('pains', index, setIdealClient)}
                    className="ml-2 text-red-600 hover:text-red-800"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
          
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Doelen
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newGoal}
                onChange={(e) => setNewGoal(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    addItem('goals', newGoal, setIdealClient);
                    setNewGoal('');
                  }
                }}
                className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Voeg doel toe..."
              />
              <button
                onClick={() => {
                  addItem('goals', newGoal, setIdealClient);
                  setNewGoal('');
                }}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                +
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {idealClient.goals?.map((item, index) => (
                <span key={index} className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                  {item}
                  <button
                    onClick={() => removeItem('goals', index, setIdealClient)}
                    className="ml-2 text-green-600 hover:text-green-800"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
          
          <div className="mt-6 flex justify-end">
            <button
              onClick={handleSaveIdealClient}
              disabled={saving}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Opslaan...' : 'Opslaan'}
            </button>
          </div>
        </div>
      )}

      {/* Value Proposition Tab */}
      {activeTab === 'valueProp' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2"><Star size={22} className="text-gray-700" /> Value Proposition</h3>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Belofte (1 zin) *
              </label>
              <input
                type="text"
                value={valueProp.promise || ''}
                onChange={(e) => setValueProp(prev => ({ ...prev, promise: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Wij helpen SaaS bedrijven hun conversie te verdubbelen..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Differentiators
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newDifferentiator}
                  onChange={(e) => setNewDifferentiator(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      addItem('differentiators', newDifferentiator, setValueProp);
                      setNewDifferentiator('');
                    }
                  }}
                  className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Voeg differentiator toe..."
                />
                <button
                  onClick={() => {
                    addItem('differentiators', newDifferentiator, setValueProp);
                    setNewDifferentiator('');
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  +
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {valueProp.differentiators?.map((item, index) => (
                  <span key={index} className="inline-flex items-center px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                    {item}
                    <button
                      onClick={() => removeItem('differentiators', index, setValueProp)}
                      className="ml-2 text-purple-600 hover:text-purple-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bewijs (cases, metrics)
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newProof}
                  onChange={(e) => setNewProof(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      addItem('proof', newProof, setValueProp);
                      setNewProof('');
                    }
                  }}
                  className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Voeg bewijs toe..."
                />
                <button
                  onClick={() => {
                    addItem('proof', newProof, setValueProp);
                    setNewProof('');
                  }}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
                >
                  +
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {valueProp.proof?.map((item, index) => (
                  <span key={index} className="inline-flex items-center px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
                    {item}
                    <button
                      onClick={() => removeItem('proof', index, setValueProp)}
                      className="ml-2 text-yellow-600 hover:text-yellow-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Elevator Pitch (1-2 zinnen) *
              </label>
              <textarea
                value={valueProp.elevator || ''}
                onChange={(e) => setValueProp(prev => ({ ...prev, elevator: e.target.value }))}
                rows={3}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="In 30 seconden: wat doe je en voor wie..."
              />
            </div>
          </div>
          
          <div className="mt-6 flex justify-end">
            <button
              onClick={handleSaveValueProp}
              disabled={saving}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Opslaan...' : 'Opslaan'}
            </button>
          </div>
        </div>
      )}

      {/* Note: Navigation handled by parent OnboardingWizard */}
    </div>
  );
}
